import { useEffect, useRef } from "react";
import "../styles/CustomCursor.css";

/**
 * PowerBESS custom cursor + electric-flux click effect.
 *
 * The click burst is procedural lightning: each arc is a jagged polyline
 * generated by midpoint-displacement subdivision, with random forking
 * branches, fired in 3 staggered waves so the discharge "crackles"
 * through empty space the way real current does — no symmetric petals,
 * no straight bolts.
 */

/* ─── Procedural lightning helpers ──────────────────────────────────── */

/** Build a jagged polyline of points from p1 → p2 using midpoint displacement. */
function lightningPoints(x1, y1, x2, y2, displacement) {
  const points = [[x1, y1]];
  const recurse = (a, b, disp, depth) => {
    if (depth > 5 || disp < 1) {
      points.push(b);
      return;
    }
    const mx = (a[0] + b[0]) / 2;
    const my = (a[1] + b[1]) / 2;
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    // unit perpendicular vector
    const nx = -dy / len;
    const ny = dx / len;
    const off = (Math.random() - 0.5) * disp;
    const mid = [mx + nx * off, my + ny * off];
    recurse(a, mid, disp * 0.55, depth + 1);
    recurse(mid, b, disp * 0.55, depth + 1);
  };
  recurse([x1, y1], [x2, y2], displacement, 0);
  return points;
}

function pointsToPath(pts) {
  if (!pts.length) return "";
  let s = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 1; i < pts.length; i++) {
    s += `L${pts[i][0].toFixed(1)},${pts[i][1].toFixed(1)}`;
  }
  return s;
}

const SVG_NS = "http://www.w3.org/2000/svg";

/** Append a single "arc" (3 stacked paths: glow / mid / core) to the svg group. */
function appendArcLayers(group, pathStr, extraClass = "") {
  ["glow", "mid", "core"].forEach((layer) => {
    const p = document.createElementNS(SVG_NS, "path");
    p.setAttribute("d", pathStr);
    p.setAttribute("class", `pbess-flux-arc pbess-flux-arc--${layer}${extraClass ? " " + extraClass : ""}`);
    group.appendChild(p);
  });
}

/* ─── Component ─────────────────────────────────────────────────────── */

export default function CustomCursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const burstLayerRef = useRef(null);
  const rafRef = useRef(0);
  const targetRef = useRef({ x: -100, y: -100 });
  const ringPos = useRef({ x: -100, y: -100 });

  useEffect(() => {
    if (typeof window === "undefined") return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    if (!fine.matches) return;

    document.body.classList.add("pbess-cursor-on");

    const dot = dotRef.current;
    const ring = ringRef.current;

    const move = (e) => {
      targetRef.current.x = e.clientX;
      targetRef.current.y = e.clientY;
      if (dot) dot.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
    };

    const setPointing = (state) => {
      if (dot) dot.classList.toggle("is-pointing", state);
      if (ring) ring.classList.toggle("is-pointing", state);
    };

    const setOnGreen = (state) => {
      if (dot) dot.classList.toggle("is-on-green", state);
      if (ring) ring.classList.toggle("is-on-green", state);
    };

    /** Walk up parents and detect if any has a lime-ish background — the
     *  PowerBESS brand colour is #CCFF00 (rgb 204,255,0). Returns true when
     *  the cursor is over any element whose computed background reads as
     *  "yellow-green" — covers lp-cta-primary, fa-cta-btn, pill active state,
     *  cookie banner accept-all, etc., without hard-coding selectors. */
    const isOnGreenSurface = (el) => {
      let node = el;
      let hops = 0;
      while (node && node.nodeType === 1 && hops < 6) {
        const cs = window.getComputedStyle(node);
        const bg = cs.backgroundColor || "";
        const m = bg.match(/rgba?\((\d+)[,\s]+(\d+)[,\s]+(\d+)(?:[,\s/]+([\d.]+))?/);
        if (m) {
          const r = +m[1];
          const g = +m[2];
          const b = +m[3];
          const a = m[4] !== undefined ? +m[4] : 1;
          // lime / yellow-green family: green >= 180, red >= 150, blue <= 90,
          // green clearly dominates blue, alpha not transparent.
          if (a > 0.5 && g >= 180 && r >= 150 && b <= 90 && (g - b) > 110) return true;
        }
        node = node.parentElement;
        hops++;
      }
      return false;
    };

    const isTextInput = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      if (tag === "textarea") return true;
      if (tag === "input") {
        const type = (el.getAttribute("type") || "text").toLowerCase();
        return ["text", "email", "password", "search", "tel", "url", "number"].includes(type);
      }
      if (el.isContentEditable) return true;
      return false;
    };

    const over = (e) => {
      const t = e.target;
      const interactive = t.closest
        ? !!t.closest("a, button, [role='button'], [role='tab'], [role='link'], .cursor-pointer, [data-cursor='pointer'], label, select, summary")
        : false;
      const textInput =
        isTextInput(t) ||
        (t.closest &&
          t.closest("input[type='text'], input[type='email'], input[type='password'], input[type='search'], input[type='tel'], input[type='url'], input[type='number'], textarea, [contenteditable='true']"));
      document.body.classList.toggle("pbess-cursor-text", !!textInput);
      setPointing(interactive && !textInput);
      setOnGreen(!textInput && isOnGreenSurface(t));
    };

    /* ─── The click flux ─── */
    const burst = (x, y) => {
      const layer = burstLayerRef.current;
      if (!layer) return;

      const node = document.createElement("div");
      node.className = "pbess-flux";
      node.style.left = `${x}px`;
      node.style.top = `${y}px`;

      // central flash only — no rings (user wants cleaner)
      node.innerHTML = '<div class="pbess-flux-flash"></div>';

      // SVG canvas for procedural lightning
      const svg = document.createElementNS(SVG_NS, "svg");
      svg.setAttribute("class", "pbess-flux-arcs");
      svg.setAttribute("viewBox", "-220 -220 440 440");
      svg.setAttribute("aria-hidden", "true");
      node.appendChild(svg);

      // small after-glow ember dots — feel like the air is still ionised
      const emberCount = 5;
      for (let i = 0; i < emberCount; i++) {
        const ang = Math.random() * Math.PI * 2;
        const dist = 10 + Math.random() * 30;
        const ember = document.createElement("span");
        ember.className = "pbess-flux-ember";
        ember.style.setProperty("--ex", `${Math.cos(ang) * dist}px`);
        ember.style.setProperty("--ey", `${Math.sin(ang) * dist}px`);
        ember.style.setProperty("--d", `${480 + Math.random() * 280}ms`);
        ember.style.setProperty("--delay", `${Math.random() * 100}ms`);
        node.appendChild(ember);
      }

      layer.appendChild(node);

      /* two waves only — cleaner, less busy */
      const waves = [
        { delay: 0,  count: 5, lifeMs: 220, maxLen: 70, minLen: 30 },
        { delay: 90, count: 3, lifeMs: 180, maxLen: 60, minLen: 22 },
      ];

      const timers = [];

      waves.forEach((wave) => {
        timers.push(
          window.setTimeout(() => {
            // some arcs in this wave will sub-flicker (re-generate themselves mid-life)
            for (let i = 0; i < wave.count; i++) {
              const ang = Math.random() * Math.PI * 2;
              const len = wave.minLen + Math.random() * (wave.maxLen - wave.minLen);
              const ex = Math.cos(ang) * len;
              const ey = Math.sin(ang) * len;

              const group = document.createElementNS(SVG_NS, "g");
              group.setAttribute("class", "pbess-flux-arc-g");
              group.style.setProperty("--lifeMs", `${wave.lifeMs}ms`);
              const startDelay = Math.random() * 50;
              group.style.animationDelay = `${startDelay}ms`;

              // main arc
              const mainPts = lightningPoints(0, 0, ex, ey, len * 0.45);
              appendArcLayers(group, pointsToPath(mainPts));

              // 0-2 branches off random interior points of the main arc
              const branchCount = Math.random() < 0.65 ? 1 : Math.random() < 0.4 ? 2 : 0;
              for (let b = 0; b < branchCount; b++) {
                if (mainPts.length < 5) break;
                const idx = 2 + Math.floor(Math.random() * (mainPts.length - 4));
                const [bx, by] = mainPts[idx];
                const baseAng = Math.atan2(ey, ex);
                const off = (Math.random() - 0.5) * 1.6; // ≈ ±46°
                const bAng = baseAng + off;
                const bLen = len * (0.32 + Math.random() * 0.32);
                const bex = bx + Math.cos(bAng) * bLen;
                const bey = by + Math.sin(bAng) * bLen;
                const bPts = lightningPoints(bx, by, bex, bey, bLen * 0.5);
                appendArcLayers(group, pointsToPath(bPts), "pbess-flux-arc--branch");
              }

              svg.appendChild(group);

              // ~35% of arcs flicker (re-generate geometry mid-life) — keeps
              // the "current jumping randomly" feel without making it busy
              if (Math.random() < 0.35) {
                timers.push(
                  window.setTimeout(() => {
                    if (!group.parentNode) return;
                    while (group.firstChild) group.removeChild(group.firstChild);
                    const ang2 = ang + (Math.random() - 0.5) * 0.9;
                    const len2 = len * (0.7 + Math.random() * 0.4);
                    const ex2 = Math.cos(ang2) * len2;
                    const ey2 = Math.sin(ang2) * len2;
                    const newPts = lightningPoints(0, 0, ex2, ey2, len2 * 0.5);
                    appendArcLayers(group, pointsToPath(newPts));
                    // optional sub-branch on the flicker
                    if (Math.random() < 0.5 && newPts.length > 4) {
                      const idx = 2 + Math.floor(Math.random() * (newPts.length - 4));
                      const [bx, by] = newPts[idx];
                      const bAng = Math.atan2(ey2, ex2) + (Math.random() - 0.5) * 1.4;
                      const bLen = len2 * (0.35 + Math.random() * 0.3);
                      const bPts = lightningPoints(bx, by, bx + Math.cos(bAng) * bLen, by + Math.sin(bAng) * bLen, bLen * 0.5);
                      appendArcLayers(group, pointsToPath(bPts), "pbess-flux-arc--branch");
                    }
                  }, wave.lifeMs * 0.35)
                );
              }

              // hard remove once life ends
              timers.push(
                window.setTimeout(() => {
                  if (group.parentNode) group.parentNode.removeChild(group);
                }, wave.lifeMs + startDelay + 60)
              );
            }
          }, wave.delay)
        );
      });

      // pulse the cursor itself in sync with the first wave
      dot && dot.classList.add("is-clicking");
      ring && ring.classList.add("is-clicking");
      timers.push(
        window.setTimeout(() => {
          dot && dot.classList.remove("is-clicking");
          ring && ring.classList.remove("is-clicking");
        }, 300)
      );

      // GC the burst node after every animation has finished
      timers.push(
        window.setTimeout(() => {
          if (node.parentNode) node.parentNode.removeChild(node);
        }, 900)
      );
    };

    const down = (e) => {
      if (isTextInput(e.target)) return;
      burst(e.clientX, e.clientY);
    };

    const leave = () => {
      if (dot) dot.style.opacity = "0";
      if (ring) ring.style.opacity = "0";
    };
    const enter = () => {
      if (dot) dot.style.opacity = "";
      if (ring) ring.style.opacity = "";
    };

    const tick = () => {
      const cur = ringPos.current;
      cur.x += (targetRef.current.x - cur.x) * 0.18;
      cur.y += (targetRef.current.y - cur.y) * 0.18;
      if (ring) ring.style.transform = `translate3d(${cur.x}px, ${cur.y}px, 0)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("mouseover", over, { passive: true });
    window.addEventListener("mousedown", down, { passive: true });
    window.addEventListener("mouseleave", leave);
    window.addEventListener("mouseenter", enter);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.body.classList.remove("pbess-cursor-on");
      document.body.classList.remove("pbess-cursor-text");
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseover", over);
      window.removeEventListener("mousedown", down);
      window.removeEventListener("mouseleave", leave);
      window.removeEventListener("mouseenter", enter);
    };
  }, []);

  return (
    <>
      <div ref={ringRef} className="pbess-cursor-ring" aria-hidden="true" data-testid="custom-cursor-ring" />
      <div ref={dotRef} className="pbess-cursor-dot" aria-hidden="true" data-testid="custom-cursor-dot">
        <svg viewBox="0 0 90 90" width="22" height="22" aria-hidden="true">
          <polygon
            points="49.63,47.85 90,45 49.81,42.16 76.82,13.18 47.84,40.19 45,0 42.15,40.33 24.99,24.99 40.33,42.15 0,45 40.19,47.84 13.18,76.82 42.16,49.81 45,90 47.85,49.63 62.84,62.84"
            fill="#CCFF00"
          />
        </svg>
      </div>
      <div ref={burstLayerRef} className="pbess-flux-layer" aria-hidden="true" data-testid="custom-cursor-flux-layer" />
    </>
  );
}
